import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const SESSION_KEY = 'agkg_session_id';

function getSessionId() {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

const useTracking = () => {
  const location = useLocation();
  const visitIdRef = useRef(null);
  const startTimeRef = useRef(null);
  const intervalRef = useRef(null);

  const sendHeartbeat = useCallback(() => {
    if (!visitIdRef.current || !startTimeRef.current) return;
    const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
    if (duration <= 0) return;
    navigator.sendBeacon('/api/track.php', JSON.stringify({
      action: 'heartbeat',
      visit_id: visitIdRef.current,
      duration: duration,
    }));
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;

    // Send final heartbeat for previous page
    if (visitIdRef.current) {
      sendHeartbeat();
    }

    // Clear previous interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    const sessionId = getSessionId();
    startTimeRef.current = Date.now();

    // Track new page visit
    fetch('/api/track.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'pageview',
        page: location.pathname,
        referrer: document.referrer || '',
        session_id: sessionId,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.visit_id) {
          visitIdRef.current = data.visit_id;
        }
      })
      .catch(() => {});

    // Heartbeat every 30 seconds
    intervalRef.current = setInterval(() => {
      if (!visitIdRef.current || !startTimeRef.current) return;
      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
      fetch('/api/track.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'heartbeat',
          visit_id: visitIdRef.current,
          duration: duration,
        }),
      }).catch(() => {});
    }, 30000);

    // Send heartbeat when tab becomes hidden
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        sendHeartbeat();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [location.pathname, sendHeartbeat]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      sendHeartbeat();
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [sendHeartbeat]);
};

export default useTracking;
